import { useNavigate } from 'react-router-dom';
import {
  FileText,
  ChevronRight,
  Link2,
  LogOut,
  Trash2,
  Users,
  Calendar,
  Settings,
  MessageCircle,
  CheckCircle2,
  Armchair,
  Image,
  MessageSquare,
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { UserAssetsSection } from '@/components/UserAssetsSection';
import { ProfileCard, ProfileCardIcon, ProfileCardStatus, TeamAvatarButton } from '@/components/profile/ProfileCard';
import { ProfileCardSkeleton } from '@/components/profile/ProfileCardSkeleton';

// Simple team member type that works with the mapped data from UserInfoScreen
interface TeamMember {
  first_name?: string | null;
  last_name?: string | null;
  avatar_url?: string | null;
  job_title?: string | null;
}

interface SocialNetwork {
  id: string;
  name: string;
  platform: string;
  connected: boolean;
  username?: string | null;
  icon: React.ComponentType<{ className?: string }>;
}

// Loading state for progressive loading
interface LoadingState {
  profile: boolean;
  team: boolean;
  knowledgeBase: boolean;
  socialNetworks: boolean;
  planning: boolean;
  shooting: boolean;
  design: boolean;
  description: boolean;
  resources: boolean;
}

interface DesktopProfileLayoutProps {
  // User data
  firstName: string;
  lastName: string;
  email: string;
  profileImage?: string;

  // Team
  assignedPBM?: TeamMember | null;
  assignedDirector?: TeamMember | null;
  assignedOps?: TeamMember | null;

  // Knowledge base
  hasKnowledgeBase: boolean;
  knowledgeBaseProgress: { completed: number; total: number };

  // Social networks
  socialNetworks: SocialNetwork[];
  disabledNetworks: string[];
  onOpenSocialNetworks: () => void;

  // Planning
  globalFrequency: number | null;
  isAutoPlanning: boolean | null;
  hasAccess: (service: string) => boolean;

  // Shooting preferences preview
  shootingPrefs: { installationType: string | null; decorColor: string | null } | null;

  // Design preferences
  thumbnailStyle: string | null;
  hasDesignPrefs: boolean;

  // Description preferences
  toneOfVoice: string | null;
  hasEmojis: boolean | null;
  hasRecurringCta: boolean | null;
  hasDescriptionPrefs: boolean;

  // Actions
  onLogout: () => void;
  onDeleteAccount: () => void;
  onEditProfile: () => void;
  onContact: () => void;

  // Progressive loading state - all default to true (loading)
  loadingState?: LoadingState;
}

function getInitials(firstName?: string | null, lastName?: string | null): string {
  const first = firstName?.charAt(0)?.toUpperCase() || '';
  const last = lastName?.charAt(0)?.toUpperCase() || '';
  return first + last || '?';
}

function getGradientFromName(firstName?: string | null, lastName?: string | null): string {
  const name = `${firstName || ''}${lastName || ''}`;
  let hash = 0;
  for (let i = 0; i < name.length; i++) {
    hash = (name.codePointAt(i) ?? 0) + ((hash << 5) - hash);
  }

  const gradients = [
    'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
    'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
    'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)',
    'linear-gradient(135deg, #fa709a 0%, #fee140 100%)',
  ];

  return gradients[Math.abs(hash) % gradients.length];
}

/**
 * Get the display text for installation type
 */
function getInstallationTypeLabel(installationType: string | null | undefined): string {
  if (installationType === 'fauteuil') return 'Fauteuil';
  if (installationType === 'tabouret') return 'Tabouret';
  return 'Bureau';
}

/**
 * Get the display text for thumbnail style
 */
function getThumbnailStyleLabel(thumbnailStyle: string | null | undefined): string {
  if (thumbnailStyle === 'bold') return 'Bold';
  if (thumbnailStyle === 'classique') return 'Classique';
  if (thumbnailStyle === 'custom') return 'Custom';
  if (thumbnailStyle === 'none') return 'Aucun';
  return 'Défini';
}

// ============================================================================
// Sub-components to reduce cognitive complexity
// ============================================================================

interface ProfileCardSectionProps {
  readonly firstName: string;
  readonly lastName: string;
  readonly email: string;
  readonly profileImage?: string;
  readonly onEditProfile: () => void;
}

function ProfileCardSection({ firstName, lastName, email, profileImage, onEditProfile }: ProfileCardSectionProps) {
  return (
    <ProfileCard key="profile-card" delay={0} hasHoverLift={false} hasGlowShadow={false} className="hover:border-[hsl(var(--v10-border-hover))] hover:shadow-lg">
      <CardContent className="p-6">
        <div className="flex items-center gap-4">
          <Avatar className="h-16 w-16 ring-2 ring-[hsl(var(--v10-border))]">
            {profileImage && <AvatarImage src={profileImage} alt="Photo de profil" className="object-cover" />}
            <AvatarFallback
              className="text-[hsl(var(--v10-text-primary))] text-xl font-medium"
              style={{ background: getGradientFromName(firstName, lastName) }}
            >
              {getInitials(firstName, lastName)}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1">
            <h3 className="text-lg font-medium text-[hsl(var(--v10-text-primary))]">{firstName} {lastName}</h3>
            <p className="text-sm text-[hsl(var(--v10-text-secondary))]">{email}</p>
            <button
              onClick={onEditProfile}
              className="text-sm text-[hsl(var(--v10-accent))] hover:text-[hsl(var(--v10-accent)/0.8)] mt-1 flex items-center gap-1 transition-colors"
            >
              Modifier le profil <span className="text-xs">→</span>
            </button>
          </div>
        </div>
      </CardContent>
    </ProfileCard>
  );
}

interface TeamMemberAvatarProps {
  readonly member: TeamMember;
  readonly delay: number;
  readonly gradientFrom: string;
  readonly gradientTo: string;
  readonly roleLabel: string;
}

function TeamMemberAvatar({ member, delay, gradientFrom, gradientTo, roleLabel }: TeamMemberAvatarProps) {
  return (
    <TeamAvatarButton delay={delay}>
      <div className="relative">
        <Avatar className="h-14 w-14 ring-2 ring-[hsl(var(--v10-border))] transition-all duration-300 group-hover:ring-[hsl(var(--v10-accent))]">
          {member.avatar_url && <AvatarImage src={member.avatar_url} alt={`${member.first_name} ${member.last_name}`} />}
          <AvatarFallback className={`bg-gradient-to-br ${gradientFrom} ${gradientTo} text-[hsl(var(--v10-text-primary))]`}>
            {member.first_name?.[0] || ''}{member.last_name?.[0] || ''}
          </AvatarFallback>
        </Avatar>
        <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-[hsl(var(--v10-accent))] rounded-full border-2 border-black" />
      </div>
      <p className="text-[hsl(var(--v10-text-primary))] text-sm font-medium mt-2 transition-colors duration-300 group-hover:text-[hsl(var(--v10-accent))]">{member.first_name}</p>
      <p className="text-[hsl(var(--v10-text-secondary))] text-xs">{roleLabel}</p>
    </TeamAvatarButton>
  );
}

interface TeamCardSectionProps {
  readonly assignedPBM?: TeamMember | null;
  readonly assignedDirector?: TeamMember | null;
  readonly assignedOps?: TeamMember | null;
  readonly onContact: () => void;
}

function TeamCardSection({ assignedPBM, assignedDirector, assignedOps, onContact }: TeamCardSectionProps) {
  const hasTeam = assignedPBM || assignedDirector || assignedOps;

  return (
    <ProfileCard key="team-card" delay={0} hasHoverLift={false} hasGlowShadow={false}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <ProfileCardIcon icon={<Users />} />
            <CardTitle className="text-[hsl(var(--v10-text-primary))] text-base">Votre équipe</CardTitle>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={onContact}
            className="glass border-[hsl(var(--v10-border))] text-[hsl(var(--v10-text-primary))] hover:bg-[hsl(var(--v10-bg-hover))] text-xs"
          >
            <MessageCircle className="h-3 w-3 mr-1" />
            Contacter
          </Button>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {hasTeam ? (
          <div className="grid grid-cols-3 gap-6 py-2">
            {assignedPBM && (
              <TeamMemberAvatar
                member={assignedPBM}
                delay={0}
                gradientFrom="from-purple-600"
                gradientTo="to-pink-500"
                roleLabel="Personal Brand Manager"
              />
            )}
            {assignedDirector && (
              <TeamMemberAvatar
                member={assignedDirector}
                delay={0.05}
                gradientFrom="from-amber-600"
                gradientTo="to-amber-500"
                roleLabel="Réalisateur"
              />
            )}
            {assignedOps && (
              <TeamMemberAvatar
                member={assignedOps}
                delay={0.1}
                gradientFrom="from-emerald-600"
                gradientTo="to-teal-500"
                roleLabel={assignedOps.job_title || 'Opérateur'}
              />
            )}
          </div>
        ) : (
          <p className="text-center text-[hsl(var(--v10-text-secondary))] text-sm py-4">Aucune équipe assignée</p>
        )}
      </CardContent>
    </ProfileCard>
  );
}

interface KnowledgeBaseCardProps {
  readonly hasKnowledgeBase: boolean;
  readonly onNavigate: () => void;
}

function KnowledgeBaseCard({ hasKnowledgeBase, onNavigate }: KnowledgeBaseCardProps) {
  return (
    <ProfileCard
      key="kb-card"
      delay={0}
      onClick={onNavigate}
      className="group"
    >
      <CardContent className="p-6 h-full flex items-center justify-between">
        <div className="flex items-center gap-4">
          <ProfileCardIcon
            icon={<FileText />}
            isComplete={hasKnowledgeBase}
            isPending={!hasKnowledgeBase}
            showCompletionBadge={true}
          />
          <div>
            <h3 className="font-medium text-[hsl(var(--v10-text-primary))]">Base de Connaissance</h3>
            <div className="mt-1">
              <ProfileCardStatus
                isComplete={hasKnowledgeBase}
                completeText="Profil stratégique complété"
              />
            </div>
          </div>
        </div>
        <ChevronRight className="h-5 w-5 text-[hsl(var(--v10-text-tertiary))]" />
      </CardContent>
    </ProfileCard>
  );
}

interface SocialNetworksCardProps {
  readonly connectedCount: number;
  readonly totalEnabled: number;
  readonly onOpenSocialNetworks: () => void;
}

function SocialNetworksCard({ connectedCount, totalEnabled, onOpenSocialNetworks }: SocialNetworksCardProps) {
  const isComplete = connectedCount === totalEnabled && totalEnabled > 0;

  return (
    <ProfileCard
      key="social-card"
      delay={0}
      onClick={onOpenSocialNetworks}
      className="group"
    >
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <ProfileCardIcon
              icon={<Link2 />}
              isComplete={isComplete}
              isPending={!isComplete}
              showCompletionBadge={true}
            />
            <div>
              <h3 className="font-medium text-[hsl(var(--v10-text-primary))]">Connexion Réseaux Sociaux</h3>
              <div className="mt-1">
                <ProfileCardStatus
                  isComplete={isComplete}
                  completeText={`${connectedCount}/${totalEnabled} connectés`}
                  incompleteText={`${connectedCount}/${totalEnabled} connectés`}
                />
              </div>
            </div>
          </div>
          <ChevronRight className="h-5 w-5 text-[hsl(var(--v10-text-tertiary))]" />
        </div>
      </CardContent>
    </ProfileCard>
  );
}

interface PlanningCardProps {
  readonly isAutoPlanning: boolean | null;
  readonly onNavigate: () => void;
}

function PlanningCard({ isAutoPlanning, onNavigate }: PlanningCardProps) {
  return (
    <ProfileCard
      key="planning-card"
      delay={0}
      onClick={onNavigate}
      className="group"
    >
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <ProfileCardIcon
              icon={<Calendar />}
              isComplete={isAutoPlanning !== null}
              isPending={isAutoPlanning === null}
              showCompletionBadge={true}
            />
            <div>
              <h3 className="font-medium text-[hsl(var(--v10-text-primary))]">Préférences de Planning</h3>
              <div className="mt-1">
                <ProfileCardStatus
                  isComplete={isAutoPlanning !== null}
                  completeText={isAutoPlanning ? 'Optimisé par Prsnl' : 'Planification manuelle'}
                />
              </div>
            </div>
          </div>
          <ChevronRight className="h-5 w-5 text-[hsl(var(--v10-text-tertiary))]" />
        </div>
      </CardContent>
    </ProfileCard>
  );
}

interface ShootingPrefsCardProps {
  readonly shootingPrefs: { installationType: string | null; decorColor: string | null } | null;
  readonly onNavigate: () => void;
}

function ShootingPrefsCard({ shootingPrefs, onNavigate }: ShootingPrefsCardProps) {
  const isComplete = !!shootingPrefs?.installationType && !!shootingPrefs?.decorColor;

  return (
    <ProfileCard
      key="shooting-card"
      delay={0}
      onClick={onNavigate}
      className="group"
    >
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <ProfileCardIcon
              icon={<Armchair />}
              isComplete={isComplete}
              isPending={!isComplete}
              showCompletionBadge={true}
            />
            <div>
              <h3 className="font-medium text-[hsl(var(--v10-text-primary))]">Préférences de tournage</h3>
              <div className="mt-1">
                <ProfileCardStatus
                  isComplete={isComplete}
                  completeText={
                    <span className="flex items-center gap-2">
                      {shootingPrefs?.decorColor && (
                        <span
                          className="w-3 h-3 rounded-full border border-white/20 inline-block"
                          style={{ backgroundColor: shootingPrefs.decorColor }}
                        />
                      )}
                      {getInstallationTypeLabel(shootingPrefs?.installationType)}
                    </span>
                  }
                />
              </div>
            </div>
          </div>
          <ChevronRight className="h-5 w-5 text-[hsl(var(--v10-text-tertiary))]" />
        </div>
      </CardContent>
    </ProfileCard>
  );
}

interface DesignPrefsCardProps {
  readonly hasDesignPrefs: boolean;
  readonly thumbnailStyle: string | null;
  readonly onNavigate: () => void;
}

function DesignPrefsCard({ hasDesignPrefs, thumbnailStyle, onNavigate }: DesignPrefsCardProps) {
  return (
    <ProfileCard
      key="design-card"
      delay={0}
      onClick={onNavigate}
      className="group"
    >
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <ProfileCardIcon
              icon={<Image />}
              isComplete={hasDesignPrefs}
              isPending={!hasDesignPrefs}
              showCompletionBadge={true}
            />
            <div>
              <h3 className="font-medium text-[hsl(var(--v10-text-primary))]">Préférences de design</h3>
              <div className="mt-1">
                <ProfileCardStatus
                  isComplete={hasDesignPrefs}
                  completeText={getThumbnailStyleLabel(thumbnailStyle)}
                />
              </div>
            </div>
          </div>
          <ChevronRight className="h-5 w-5 text-[hsl(var(--v10-text-tertiary))]" />
        </div>
      </CardContent>
    </ProfileCard>
  );
}

interface DescriptionPrefsCardProps {
  readonly hasDescriptionPrefs: boolean;
  readonly toneOfVoice: string | null;
  readonly hasEmojis: boolean | null;
  readonly hasRecurringCta: boolean | null;
  readonly onNavigate: () => void;
}

function DescriptionPrefsCard({ hasDescriptionPrefs, toneOfVoice, hasEmojis, hasRecurringCta, onNavigate }: DescriptionPrefsCardProps) {
  return (
    <ProfileCard
      key="description-card"
      delay={0}
      onClick={onNavigate}
      className="group"
    >
      <CardContent className="p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <ProfileCardIcon
              icon={<MessageSquare />}
              isComplete={hasDescriptionPrefs}
              isPending={!hasDescriptionPrefs}
              showCompletionBadge={true}
            />
            <div>
              <h3 className="font-medium text-[hsl(var(--v10-text-primary))]">Préférences de description</h3>
              <div className="mt-1">
                {hasDescriptionPrefs ? (
                  <div className="flex items-center gap-1 text-xs text-[hsl(var(--v10-accent))]">
                    <CheckCircle2 className="w-3 h-3 shrink-0" />
                    <span>{toneOfVoice === 'tu' ? 'Tutoiement' : 'Vouvoiement'}</span>
                    <span className="text-[hsl(var(--v10-text-tertiary))]">·</span>
                    <span>{hasEmojis ? 'Emojis' : 'Sans emojis'}</span>
                    <span className="text-[hsl(var(--v10-text-tertiary))]">·</span>
                    <span>{hasRecurringCta ? 'CTA' : 'Sans CTA'}</span>
                  </div>
                ) : (
                  <ProfileCardStatus isComplete={false} />
                )}
              </div>
            </div>
          </div>
          <ChevronRight className="h-5 w-5 text-[hsl(var(--v10-text-tertiary))]" />
        </div>
      </CardContent>
    </ProfileCard>
  );
}

interface AccountSectionProps {
  readonly onLogout: () => void;
  readonly onDeleteAccount: () => void;
}

function AccountSection({ onLogout, onDeleteAccount }: AccountSectionProps) {
  return (
    <ProfileCard delay={0} hasHoverLift={false} hasGlowShadow={false}>
      <CardHeader className="pb-3">
        <div className="flex items-center gap-3">
          <ProfileCardIcon icon={<Settings />} />
          <CardTitle className="text-[hsl(var(--v10-text-primary))] text-base">Mon Compte</CardTitle>
        </div>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-2 gap-4">
          <Button
            variant="ghost"
            onClick={onLogout}
            className="w-full glass border-[hsl(var(--v10-border))] text-[hsl(var(--v10-text-primary))] hover:bg-[hsl(var(--v10-bg-hover))] h-12"
          >
            <LogOut className="h-4 w-4 mr-2" />
            Se déconnecter
          </Button>
          <Button
            variant="ghost"
            onClick={onDeleteAccount}
            className="w-full glass border-red-500/30 bg-red-500/10 text-red-400 hover:bg-red-500/20 h-12"
          >
            <Trash2 className="h-4 w-4 mr-2" />
            Supprimer mon compte
          </Button>
        </div>
        <p className="text-center text-[hsl(var(--v10-text-tertiary))] text-xs mt-4">
          Besoin d'aide ? Contactez notre support avant de supprimer votre compte.
        </p>
      </CardContent>
    </ProfileCard>
  );
}

// ============================================================================
// Main Component
// ============================================================================

export function DesktopProfileLayout({
  firstName,
  lastName,
  email,
  profileImage,
  assignedPBM,
  assignedDirector,
  assignedOps,
  hasKnowledgeBase,
  socialNetworks,
  disabledNetworks,
  onOpenSocialNetworks,
  isAutoPlanning,
  hasAccess,
  shootingPrefs,
  thumbnailStyle,
  hasDesignPrefs,
  toneOfVoice,
  hasEmojis,
  hasRecurringCta,
  hasDescriptionPrefs,
  onLogout,
  onDeleteAccount,
  onEditProfile,
  onContact,
  loadingState,
}: Readonly<DesktopProfileLayoutProps>) {
  const navigate = useNavigate();

  // Filter to only enabled networks (not in disabled list)
  const enabledNetworks = socialNetworks.filter(n => !disabledNetworks.includes(n.platform));
  const connectedCount = enabledNetworks.filter(n => n.connected).length;
  const totalEnabled = enabledNetworks.length;

  // Destructure loading states with defaults to TRUE (showing skeleton)
  const {
    profile: isLoadingProfile = true,
    team: isLoadingTeam = true,
    knowledgeBase: isLoadingKnowledgeBase = true,
    socialNetworks: isLoadingSocialNetworks = true,
    planning: isLoadingPlanning = true,
    shooting: isLoadingShooting = true,
    design: isLoadingDesign = true,
    description: isLoadingDescription = true,
    resources: isLoadingResources = true,
  } = loadingState || {};

  const showPlanningCard = hasAccess('admin_planning_setup') || hasAccess('admin_rescheduling_setup');

  return (
    <div className="min-h-screen p-8">
      <div className="max-w-5xl mx-auto space-y-6">
        {/* Header */}
        <motion.div
          className="mb-8"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          <h1 className="text-2xl font-semibold text-[hsl(var(--v10-text-primary))]">Votre profil</h1>
          <p className="text-[hsl(var(--v10-text-secondary))]">Gérez vos informations et préférences</p>
        </motion.div>

        {/* Row 1: Profile Card + Team Card */}
        <div className="grid grid-cols-2 gap-6">
          <AnimatePresence mode="wait">
            {isLoadingProfile ? (
              <ProfileCardSkeleton type="profile" key="profile-skeleton" />
            ) : (
              <ProfileCardSection
                firstName={firstName}
                lastName={lastName}
                email={email}
                profileImage={profileImage}
                onEditProfile={onEditProfile}
              />
            )}
          </AnimatePresence>

          <AnimatePresence mode="wait">
            {isLoadingTeam ? (
              <ProfileCardSkeleton type="team" key="team-skeleton" />
            ) : (
              <TeamCardSection
                assignedPBM={assignedPBM}
                assignedDirector={assignedDirector}
                assignedOps={assignedOps}
                onContact={onContact}
              />
            )}
          </AnimatePresence>
        </div>

        {/* Row 2: Knowledge Base + Resources */}
        <div className="grid grid-cols-2 gap-6">
          <AnimatePresence mode="wait">
            {isLoadingKnowledgeBase ? (
              <ProfileCardSkeleton type="simple" key="kb-skeleton" />
            ) : (
              <KnowledgeBaseCard
                hasKnowledgeBase={hasKnowledgeBase}
                onNavigate={() => navigate('/app/profile/knowledge-base')}
              />
            )}
          </AnimatePresence>

          <UserAssetsSection delay={0} isLoading={isLoadingResources} />
        </div>

        {/* Row 3: Social Networks + Planning */}
        <div className="grid grid-cols-2 gap-6">
          <AnimatePresence mode="wait">
            {isLoadingSocialNetworks ? (
              <ProfileCardSkeleton type="simple" key="social-skeleton" />
            ) : (
              <SocialNetworksCard
                connectedCount={connectedCount}
                totalEnabled={totalEnabled}
                onOpenSocialNetworks={onOpenSocialNetworks}
              />
            )}
          </AnimatePresence>

          {showPlanningCard && (
            <AnimatePresence mode="wait">
              {isLoadingPlanning ? (
                <ProfileCardSkeleton type="simple" key="planning-skeleton" />
              ) : (
                <PlanningCard
                  isAutoPlanning={isAutoPlanning}
                  onNavigate={() => navigate('/app/admin-planning-setup')}
                />
              )}
            </AnimatePresence>
          )}
        </div>

        {/* Row 4: Shooting + Design Preferences */}
        <div className="grid grid-cols-2 gap-6">
          <AnimatePresence mode="wait">
            {isLoadingShooting ? (
              <ProfileCardSkeleton type="simple" key="shooting-skeleton" />
            ) : (
              <ShootingPrefsCard
                shootingPrefs={shootingPrefs}
                onNavigate={() => navigate('/app/profile/shooting-preferences')}
              />
            )}
          </AnimatePresence>

          <AnimatePresence mode="wait">
            {isLoadingDesign ? (
              <ProfileCardSkeleton type="simple" key="design-skeleton" />
            ) : (
              <DesignPrefsCard
                hasDesignPrefs={hasDesignPrefs}
                thumbnailStyle={thumbnailStyle}
                onNavigate={() => navigate('/app/profile/design-preferences')}
              />
            )}
          </AnimatePresence>
        </div>

        {/* Row 5: Description Preferences */}
        <AnimatePresence mode="wait">
          {isLoadingDescription ? (
            <ProfileCardSkeleton type="simple" key="description-skeleton" />
          ) : (
            <DescriptionPrefsCard
              hasDescriptionPrefs={hasDescriptionPrefs}
              toneOfVoice={toneOfVoice}
              hasEmojis={hasEmojis}
              hasRecurringCta={hasRecurringCta}
              onNavigate={() => navigate('/app/profile/description-preferences')}
            />
          )}
        </AnimatePresence>

        {/* Row 6: Account Section - Full width */}
        <AccountSection onLogout={onLogout} onDeleteAccount={onDeleteAccount} />
      </div>
    </div>
  );
}
